import Anchor from "atoms/anchors/anchor";
import Button from "atoms/buttons/button";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import PublicationRecord from "models/view-models/publication-record";
import UserPublicationFavoriteRecord from "models/view-models/user-publication-favorite-record";
import PublicationOptionsMenu from "organisms/modals/publication-selection-modal/publication-options-menu";
import { PublicationSelectionListProps } from "organisms/modals/publication-selection-modal/publication-selection-list";
import ReadonlyPublicationInfoModal from "organisms/modals/readonly-publication-info-modal";
import * as React from "react";
import { Fragment, useState } from "react";
import publicationContentFormatType from "utilities/enumerations/publication-content-format-type";
import { useLocalization } from "utilities/hooks/use-localization";
import StringUtils from "utilities/string-utils";
import usePublicationCover from "utilities/hooks/domain/publications/use-publication-cover";

export interface DashboardPublicationsListItemProps
    extends Pick<PublicationSelectionListProps, "displayMenuActions"> {
    favorites?: Array<UserPublicationFavoriteRecord>;
    onChangeFavorites: (newValue: Array<UserPublicationFavoriteRecord>) => void;
    publication: PublicationRecord;
    onResultClick?: Function;
    cssClassModifier?: string;
}

const BASE_CLASS = "c-dashboard-publications-list-item";

const DashboardPublicationsListItem = (
    props: DashboardPublicationsListItemProps
) => {
    const {
        displayMenuActions = true,
        favorites = [],
        publication,
        onChangeFavorites,
        onResultClick = () => {},
        cssClassModifier = "",
    } = props;

    const { t } = useLocalization();

    const { coverImageSrc } = usePublicationCover(publication);

    const coverStyle: React.CSSProperties = StringUtils.hasValue(coverImageSrc)
        ? {
              backgroundImage: `url(${coverImageSrc})`,
              backgroundSize: "cover",
          }
        : {};

    const [showLegacyInfoModal, setShowLegacyInfoModal] =
        useState<boolean>(false);

    return (
        <div className={`${BASE_CLASS} ${cssClassModifier}`}>
            <div className={`${BASE_CLASS}__item_parent`}>
                <Anchor
                    onClick={(e) => onResultClick()}
                    cssClassName={`${BASE_CLASS}__item`}
                    to={publication.getRoute()}>
                    <div
                        className={`${BASE_CLASS}__cover`}
                        style={coverStyle}
                    />
                    <div>
                        <PublicationText publication={publication} />
                    </div>
                </Anchor>
                {displayMenuActions && (
                    <>
                        <div className={`${BASE_CLASS}__actions`}>
                            {publication.publicationContentFormatType ===
                                publicationContentFormatType.Image && (
                                <Button
                                    accessibleText={t(
                                        "readonlyPublicationAboutTitle"
                                    )}
                                    onClick={() => setShowLegacyInfoModal(true)}
                                    cssClassName={"c-action-button"}
                                    size={ButtonSizes.Small}>
                                    <Icon
                                        cssClassName="c-legacy-icon"
                                        type={Icons.Scroll}
                                        size={IconSizes.Base}
                                    />
                                    <ReadonlyPublicationInfoModal
                                        isVisible={showLegacyInfoModal}
                                        closeDialog={() =>
                                            setShowLegacyInfoModal(false)
                                        }
                                    />
                                </Button>
                            )}

                            <PublicationOptionsMenu
                                favorites={favorites}
                                onChangeFavorites={onChangeFavorites}
                                publication={publication}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

function PublicationText(
    props: Pick<DashboardPublicationsListItemProps, "publication">
) {
    const { publication } = props;

    const edition = (
        <div className={`${BASE_CLASS}__item__title`}>{publication.title}</div>
    );

    const title = (
        <div className={`${BASE_CLASS}__item__edition`}>
            {publication.getDisplayCodeAndEdition()}
        </div>
    );

    return (
        <Fragment>
            {title}
            {edition}
        </Fragment>
    );
}

export default DashboardPublicationsListItem;
